import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { BaseCheckboxColor } from 'shared/components/base/form/base-checkbox/base-checkbox-color.enum'
import BaseRadioButton from 'shared/components/base/form/base-radio-button'
import BaseSelect, { OptionType } from 'shared/components/base/form/base-select'
import BaseLabel from 'shared/components/base/form/BaseLabel'
import dictionary from 'shared/constants/dictionary'
import { useEmit } from 'shared/contexts/socket/socket-context'
import {
  setCallType,
  setFormData,
  setLeadSource,
  setPreLeadName,
  setTransferredBusinessUnits,
} from 'store/slice/call-detail/call-detail.slice'
import { PhoneCallType } from 'store/slice/call-detail/types'
import { RootState } from 'store/store'

import CustomerSearchInput from './customer-search-input'

/*----types---*/
type Option = {
  label: string
  value: string
  key: string
}
/*----types---*/

const options: Option[] = [
  { label: 'لیدفروش', value: PhoneCallType.LEAD, key: PhoneCallType.LEAD },
  { label: 'منبع', value: PhoneCallType.SOURCE, key: PhoneCallType.SOURCE },
  {
    label: 'پیگیری ‌و شکایات',
    value: PhoneCallType.CRITICISM,
    key: PhoneCallType.CRITICISM,
  },
  {
    label: 'اتصال‌ به‌ فرد/ واحد',
    value: PhoneCallType.TRANSFER,
    key: PhoneCallType.TRANSFER,
  },
  { label: 'سایر', value: PhoneCallType.OTHER, key: PhoneCallType.OTHER },
]

const CallSubmissionFormContentHeader = () => {
  const dispatch = useDispatch()
  const emit = useEmit()
  const [leadSources, setLeadSources] = useState<
    { label: string; value: number; key: number }[]
  >([])
  const [preNameOptions, setPreNameOptions] = useState<OptionType<number>[]>([])

  const { formData, callData } = useSelector(
    (state: RootState) => state.callDetail
  )
  const activeCallType = useSelector(
    (state: RootState) => state.callDetail.formData.callType
  )
  const [isContactNew, setIsContactNew] = useState<boolean>(false)
  const isOverLoaded = formData.isOverload
  const isCooperationRequest = formData.isCooperationRequest

  useEffect(() => {
    dispatch(setLeadSource(100000005))
    emit('getLeadPreNames')
      .then((options: { label: string; value: number }[]) => {
        options.sort((a, b) => {
          if (a.label === 'حاج آقا') return 1
          if (b.label === 'حاج آقا') return -1
          return a.label <= b.label ? -1 : 1
        })
        const _options = options.map((item) => ({
          ...item,
          key: item.value,
        }))
        setPreNameOptions(_options)
      })
      .catch(() => {
        toast.error(dictionary.socket_timeout)
      })
  }, [])

  useEffect(() => {
    if (!callData.preName?.value) {
      if (callData.phoneNumber?.startsWith('09')) {
        dispatch(setPreLeadName(100000004))
      } else {
        dispatch(setPreLeadName(100000000))
      }
    }
  }, [callData.phoneNumber])

  const handleSelect = (value: PhoneCallType) => {
    dispatch(setCallType(value))
  }

  const sortSources = (items: { label: string; value: number }[]) => {
    const importantKeys = [
      'سایت شرکت',
      'بیلبورد',
      'دوستان و همکاران',
      'اینستاگرام',
      'تلگرام',
      'عدم پاسخ',
    ]
    return items.sort((item) => (importantKeys.includes(item.label) ? -1 : 1))
  }

  const fetchLeadSourceOptions = () => {
    emit('lead-source')
      .then((leadSources: { label: string; value: number }[]) => {
        const sortedSources = sortSources(leadSources)
        if (!sortedSources.length) {
          return fetchLeadSourceOptions()
        }

        setLeadSources(
          sortedSources.map((leadSource) => ({
            ...leadSource,
            key: leadSource.value,
          }))
        )
      })
      .catch(() => {
        toast.error(dictionary.socket_timeout)
      })
  }

  useEffect(fetchLeadSourceOptions, [])

  const isRadioButtonChecked = (option: Option) => {
    if (option.value === formData.callType) {
      if (option.label === 'پیگیری ‌و شکایات') {
        dispatch(setTransferredBusinessUnits('بازرسی'))
      }
      return true
    }
  }

  const checkIsOverLoaded = () => {
    dispatch(
      setFormData({
        isOverload: !isOverLoaded,
        isCooperationRequest: false,
      })
    )
  }

  const checkIsCooperationRequest = () => {
    dispatch(
      setFormData({
        isOverload: false,
        isCooperationRequest: !isCooperationRequest,
      })
    )
  }

  useEffect(() => {
    if (activeCallType !== PhoneCallType.CRITICISM) {
      dispatch(setTransferredBusinessUnits(''))
    }
  }, [activeCallType])

  return (
    <>
      <p className="mb-1 text-title ">نوع تماس*</p>
      <div className="grid grid-cols-9 sm:pl-[15px] xl:pl-10   items-center  ">
        <div className={' col-span-9 py-3 '}>
          <div className={'flex justify-between gap-2'}>
            {options.map((option: Option, index: number) => {
              return (
                <div
                  key={index}
                  className={'flex gap-1'}
                  onClick={() => handleSelect(option.value as PhoneCallType)}
                >
                  <BaseRadioButton
                    className={classNames(
                      'w-[16px] h-[16px] ',
                      isRadioButtonChecked(option) ? '!bg-[#109CF1]' : null
                    )}
                    color={BaseCheckboxColor.PersianBlue}
                    checked={isRadioButtonChecked(option)}
                  />
                  <BaseLabel className={'text-[0.7rem] font-normal'}>
                    {option.label}
                  </BaseLabel>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      {callData?.isNewCustomer && (
        <>
          <div className="bg-[#F4F4F6] h-[0.19rem] my-4  mr-[-0.7rem] ml-[-0.88rem]" />
          <div className="flex flex-row justify-between">
            <div className="flex">
              {isContactNew ||
              !callData.leadId ||
              !callData.contactId ||
              !callData.accountId ? (
                <BaseSelect<number>
                  limitOptionsHeight={false}
                  options={preNameOptions}
                  value={formData.preLeadName || 0}
                  className={
                    'w-[100px] focus-within:border-white focus-visible:border-white rounded-none rounded-l-none'
                  }
                  setInputClassName={() => classNames('  rounded-l-none !h-10')}
                  label={`نام ${
                    activeCallType !== 'other' && activeCallType !== 'transfer'
                      ? '*'
                      : ''
                  }`}
                  onSelect={(value) => dispatch(setPreLeadName(value))}
                />
              ) : null}
              <div
                className={classNames(
                  'w-full  rounded-none focus-within:outline-none focus-within:border-white ',
                  isContactNew ||
                    !callData.leadId ||
                    !callData.contactId ||
                    !callData.accountId
                    ? ''
                    : ''
                )}
              >
                <CustomerSearchInput
                  label={
                    isContactNew ||
                    !callData.leadId ||
                    !callData.contactId ||
                    !callData.accountId ? (
                      <div style={{ visibility: 'hidden' }}>test</div>
                    ) : (
                      'نام*'
                    )
                  }
                  inputClassName={classNames(
                    isContactNew ||
                      !callData.leadId ||
                      !callData.contactId ||
                      !callData.accountId
                      ? ' min-h-[2.6rem] md:w-[200px]  hover:border-gray-light border-gray-light  rounded-r-none border-r-white hover:border-r-white  '
                      : null
                  )}
                  setIsNew={setIsContactNew}
                  isNew={isContactNew}
                />
              </div>
            </div>
            {[PhoneCallType.LEAD].includes(formData.callType) &&
              !formData.selectedCustomer && (
                <BaseSelect<number>
                  className="w-[159px]"
                  label="نحوه آشنایی*"
                  options={leadSources}
                  value={formData.leadSource}
                  onSelect={(value) => {
                    dispatch(setLeadSource(value))
                  }}
                  defaultValue={formData.leadSource}
                  isLoading={!leadSources.length}
                />
              )}
          </div>
        </>
      )}

      {formData.callType === PhoneCallType.SOURCE && (
        <>
          {!callData?.isNewCustomer && (
            <div className="bg-[#F4F4F6] h-[0.19rem] mt-2 mb-4  mr-[-0.7rem] ml-[-0.88rem]" />
          )}
          <div className="flex items-center w-full">
            <div
              className="flex items-center cursor-pointer"
              onClick={checkIsOverLoaded}
            >
              <BaseRadioButton className="ml-2" checked={isOverLoaded} />
              <p className="inline text-sm font-normal leading-8 text-color-title ml-28">
                مازاد بار دارد.
              </p>
            </div>
            <div
              className="flex items-center cursor-pointer"
              onClick={checkIsCooperationRequest}
            >
              <BaseRadioButton
                className="ml-2"
                checked={isCooperationRequest}
              />
              <p className="inline text-sm font-normal leading-8 text-color-title">
                درخواست همکاری با مجموعه را دارد
              </p>
            </div>
          </div>
        </>
      )}

      {/*divider*/}
      <div className="bg-[#F4F4F6] h-[0.19rem] mr-[-0.7rem] ml-[-0.88rem] my-4" />
      {/*divider*/}
    </>
  )
}

export default CallSubmissionFormContentHeader
